import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import useGeolocation from '../hooks/useGeolocation'
import {
  type CreateSample,
  FetchError,
  type Geometry,
  longitudeLatitudeToGeometry,
  SampleFormData,
  SaveError,
  TopBar,
  AppLayout,
  useCreateSampleMutation,
  useGetSampleByIdQuery,
  useGetTaskByIdQuery,
  useGoBack,
  UserFeedback,
  Mtds,
  parsePayloadSchema
} from 'prove-common'
import SampleRegistrationForm from '../components/sample-registration-form/SampleRegistrationForm'
import {
  createSamplePayload,
  createSubSamples,
  createSubsamplesByNumber,
  getExternalReference,
  getExternalReferenceType,
  isNameRedacted
} from '../utils/sampleUtils'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SampleInfoCard } from '../components/sample-info-card/SampleInfoCard'
import LocationFinderCard, {
  ACCEPTABLE_ACCURACY_METERS
} from '../components/location-finder-card/LocationFinderCard'
import { IChangeEvent } from 'prove-common/rjsf'

const SampleRegistrationScreen = () => {
  const navigate = useNavigate()
  const position = useGeolocation()
  const { sampleId } = useParams()
  const search = useLocation().search
  const taskId = new URLSearchParams(search).get('taskId')
  const goBack = useGoBack(taskId ? `/tasks/${taskId}` : '/tasks')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<unknown>(null)
  const [hasCheckedCode, setHasCheckedCode] = useState(false)
  const [selectedLonLat, setSelectedLonLat] = useState<
    [number, number] | undefined
  >()
  const {
    data: task,
    isPending: isPendingTask,
    isError: isErrorTask
  } = useGetTaskByIdQuery(Number(taskId))
  const {
    isSuccess: isSuccessSample,
    isPending: isPendingSample,
    isError: isErrorSample,
    error: errorSample
  } = useGetSampleByIdQuery(sampleId ?? undefined)
  const createSample = useCreateSampleMutation()
  let manualPosition: GeolocationPosition | undefined = undefined
  if (selectedLonLat != null) {
    manualPosition = {
      coords: {
        latitude: selectedLonLat[1],
        longitude: selectedLonLat[0]
      }
    } as GeolocationPosition
  }
  let geometry: Geometry | undefined
  let accuratePosition = false
  if (manualPosition != null) {
    geometry = longitudeLatitudeToGeometry({
      longitude: manualPosition.coords.longitude,
      latitude: manualPosition.coords.latitude
    })
    accuratePosition = true
  } else if (position != null) {
    geometry = longitudeLatitudeToGeometry({
      longitude: position.coords.longitude,
      latitude: position.coords.latitude
    })
    accuratePosition = position.coords.accuracy <= ACCEPTABLE_ACCURACY_METERS
  }
  useEffect(() => {
    if (!hasCheckedCode) {
      if (isSuccessSample) {
        Mtds.toast.danger('Koden er allerede i bruk')
        goBack()
      } else if (isErrorSample) {
        if (errorSample instanceof FetchError && errorSample.status !== 404) {
          Mtds.toast.danger('Kunne ikke sjekke koden')
          goBack()
        }
        setHasCheckedCode(true)
      }
    }
  }, [isSuccessSample, isErrorSample, hasCheckedCode, goBack])

  const externalReferenceType = getExternalReferenceType(task?.sampntType.code)
  const onSubmit = (data: IChangeEvent<SampleFormData>): void => {
    const formData = data.formData
    const sampleTypeId = task?.analysisType?.sampleType?.id
    if (
      formData != null &&
      task != null &&
      sampleTypeId != null &&
      sampleId != null &&
      !isSaving
    ) {
      const nameRedacted = isNameRedacted(formData.nameRedacted)
      if (nameRedacted == null) {
        Mtds.toast.danger(
          'Svar på om virksomhetsnavnet kun inneholder personnavn'
        )
        return
      }
      const externalReference = getExternalReference(
        formData,
        externalReferenceType
      )
      const orgnr = externalReference?.organisasjonsnummer
      const parsedOrgnr =
        orgnr != null && orgnr.trim() !== '' && !Number.isNaN(Number(orgnr))
          ? Number(orgnr)
          : undefined
      const payload = formData.payload
      const createSubSamplesList = payload?.subSamplesNumber
        ? createSubsamplesByNumber(payload)
        : createSubSamples(payload?.subSamples ?? [])

      const samplePayload = createSamplePayload(
        payload ?? {},
        externalReferenceType === 'PRODUSENT'
          ? externalReference.externalReferenceId
          : undefined
      )
      setIsSaving(true)
      setSaveError(null)
      const sample: CreateSample = {
        sampleTypeId,
        takenDate: new Date(formData.takenDate).toISOString(),
        taskId: task.id,
        externalReferenceType: externalReferenceType,
        externalReferenceId: externalReference?.externalReferenceId,
        orgnr: parsedOrgnr,
        oversightObject: externalReference?.tilsynsobjektId,
        sampntTypeId: task.sampntType.id,
        comment: formData.comment,
        geometry,
        payload: samplePayload,
        nameRedacted,
        subSamples: createSubSamplesList,
        accuratePosition
      }
      createSample
        .mutateAsync({
          sampleId,
          body: sample
        })
        .then(() => {
          navigate(`/samples/${sampleId}/registration-completed`, {
            replace: true
          })
        })
        .catch((error) => {
          setIsSaving(false)
          setSaveError(error)
        })
    } else {
      Mtds.toast.danger(
        'Kunne ikke registrere prøve. Kontakt brukerstøtte hvis problemet vedvarer.'
      )
      setIsSaving(false)
    }
  }
  const taskDescription = task?.description
  const isPending = isPendingTask || isPendingSample

  return (
    <AppLayout
      isLoading={isPending}
      isError={isErrorTask}
      topBar={<TopBar title='Fyll ut detaljer om prøven' logo={<AppLogo />} />}
    >
      <Mtds.Grid>
        {task?.locationRequired === true && (
          <LocationFinderCard
            locationRequired={task.locationRequired}
            position={manualPosition ?? position}
            sampleId={sampleId}
            newSample={true}
            onSelectedLonLatChanged={setSelectedLonLat}
          />
        )}

        <SampleInfoCard
          sampleId={sampleId}
          matrix={task?.analysisType?.sampleType?.matrix}
          description={taskDescription}
        />
        {sampleId != null && taskId != null && (
          <Mtds.Card as={Mtds.Grid} data-gap={5}>
            <Mtds.Heading as='h3'>Fyll ut skjema</Mtds.Heading>
            <SaveError error={saveError} action='registrere prøven' />
            <SampleRegistrationForm
              schema={parsePayloadSchema(
                task?.analysisType?.payloadSchema?.schema
              )}
              externalReferenceType={externalReferenceType}
              onSubmit={onSubmit}
              onCancel={goBack}
              isSaving={isSaving}
              isEditing={false}
              position={manualPosition ?? position}
            />
          </Mtds.Card>
        )}
      </Mtds.Grid>
      <UserFeedback appName='proveta' />
    </AppLayout>
  )
}

export default SampleRegistrationScreen
